import React, { useRef } from 'react'

import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import TextField from '@material-ui/core/TextField'

import { getInfiniteMinimalContacts } from 'src/graphql'
import { Button, Icon } from 'src/chrome'
import { Group } from 'src/contacts/contactTypes'
import { useQueries, useScroll, useState } from 'src/hooks'

import GroupSelect from '../GroupSelect/GroupSelect'
import styles from './groupAddCreate.module.scss'
import { CircularProgress } from '@material-ui/core'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

type GroupAddCreateProps = {
  initialMode: 'create' | 'select'
  contactsIds: string[]
  groups: Group[]
  onClose: () => void
  onCreateGroup?: (group: Omit<Group, 'id'>) => void
  onSelect?: (group: Group) => void
}

const GroupAddCreate: React.FC<GroupAddCreateProps> = props => {
  const {
    initialMode,
    contactsIds,
    groups,
    onClose,
    onCreateGroup,
    onSelect,
  } = props

  const [mode, setMode] = useState(initialMode)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<string>()

  const [{ results: contacts, ...scrollOptions }] = useQueries(
    getInfiniteMinimalContacts({
      contacts: contactsIds,
    }),
  )

  const selectedGroup = groups.find(group => group.id === selected)
  const triggerElement = useRef<HTMLDivElement>(null)
  const handleScroll = useScroll({
    ...scrollOptions,
    triggerElement,
  })

  return (
    <div className={styles.container}>
      <span className={styles.close} onClick={() => onClose()}>
        <Icon icon={'CLOSE'} color={'#6f8394'} size={18} />
      </span>
      <div className={styles.body}>
        <div className={styles.selectorsBlock}>
          {onSelect && mode !== 'select' && (
            <Button
              id={'create_or_select_group_btn'}
              title={'Select Group'}
              iconColor={'white'}
              iconSize={16}
              buttonColor={'pink'}
              style={{ marginBottom: 20 }}
              onClick={() => setMode('select')}
            />
          )}

          {onCreateGroup && mode !== 'create' && (
            <Button
              id={'create_or_select_group_btn'}
              title={'Create Group'}
              iconColor={'white'}
              iconSize={16}
              buttonColor={'pink'}
              style={{ marginBottom: 20 }}
              onClick={() => setMode('create')}
            />
          )}

          {mode === 'create' && (
            <div className={styles.newGroupContainer}>
              <Text type="largeBody" color="primaryHeading">
                New Group
              </Text>
              <Spacer space="x1" />
              <TextField
                id={'group_name_field'}
                label="Group Name"
                InputLabelProps={{
                  classes: {
                    root: styles.inputLabel,
                  },
                }}
                InputProps={{
                  classes: {
                    root: styles.input,
                  },
                }}
                value={name}
                onChange={e => setName(e.target.value)}
                className={styles.textField}
              />
              <TextField
                id={'group_description_field'}
                label="Description"
                InputLabelProps={{
                  classes: {
                    root: styles.inputLabel,
                  },
                }}
                InputProps={{
                  classes: {
                    root: styles.input,
                  },
                }}
                value={description}
                multiline={true}
                onChange={e => setDescription(e.target.value)}
                className={styles.textField}
              />
            </div>
          )}

          {mode === 'select' && (
            <div className={styles.groupSelectContainer}>
              <GroupSelect
                groups={groups}
                onGroupChange={group => setSelected(group.id)}
                shouldHighlightSelected={true}
              />
            </div>
          )}
        </div>

        <div className={styles.selectedContacts}>
          <Text type="largeBody" color="primaryHeading">{`${
            contactsIds.length
          } contact${contactsIds.length === 1 ? '' : 's'} selected`}</Text>
          <List className={styles.contactSelector} onScroll={handleScroll}>
            {contacts.map(contact => (
              <ListItem key={contact.id}>
                <ListItemText
                  primary={
                    <Text type="largeBody">
                      {contact.firstName} {contact.lastName}
                    </Text>
                  }
                />
              </ListItem>
            ))}
            {scrollOptions.hasMore && (
              <div
                ref={triggerElement}
                style={{ background: 'transparent', boxShadow: 'none' }}
              >
                <CircularProgress />
              </div>
            )}
          </List>
        </div>
      </div>
      <div className={styles.actions}>
        {mode === 'select' && selectedGroup && (
          <div style={{ marginRight: 25 }}>
            Add contacts to group: {selectedGroup.name}
          </div>
        )}

        {onCreateGroup && mode === 'create' && (
          <Button
            id="submit_new_group_btn"
            title="Submit"
            buttonColor="pink"
            disabled={!name}
            onClick={() => onCreateGroup({ name, description })}
          />
        )}

        {onSelect && mode === 'select' && (
          <Button
            id="submit_new_group_btn"
            title="Submit"
            buttonColor="pink"
            disabled={!selectedGroup}
            onClick={selectedGroup ? () => onSelect(selectedGroup) : undefined}
          />
        )}
      </div>
    </div>
  )
}

export default GroupAddCreate
